import { useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Progress } from 'antd';
import * as userActions from "../../reduxStore/actions/userActionsHelper.js";
import { userError$, userLoading$, userSignupSuccess$ } from '../../reduxStore/selectors/userSelectors';
import { clearSignupSuccess } from '../../reduxStore/reducers/userReducersHelper';
import styles from "./asset/signUpForm.module.scss";
import { Link, useNavigate } from "react-router-dom";
import { signUpValidationRules, validateForm } from "../../utils/formValidation";
import { validateStrongPassword } from "../../utils/validation";
import TermsOfServiceModal from "../termsOfServiceModal/termsOfServiceModal.js";
import PrivacyPolicyModal from "../privacyPolicyModal/privacyPolicyModal.js";
import { isFeatureEnabled, FEATURES } from "../../utils/featureFlags.js";

const SignUpFormComponent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [signUpData, setSignUpData] = useState({});
  const [showTosModal, setShowTosModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [hasReadTos, setHasReadTos] = useState(false);
  const [hasReadPrivacy, setHasReadPrivacy] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const userError = useSelector(userError$);
  const isLoading = useSelector(userLoading$);
  const isSignupSuccess = useSelector(userSignupSuccess$);

  const validateFormData = () => {
    const validation = validateForm(signUpData, signUpValidationRules);
    setErrors(validation.errors);
    return validation.isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    if (!agreedToTerms) {
      setErrors({ terms: 'You must read and agree to the Terms of Service and Privacy Policy' });
      return;
    }

    if (!validateFormData()) return;

    dispatch(userActions.createUser.request(signUpData));
  };

  const handleTosOpen = () => {
    setShowTosModal(true);
  };

  const handleTosClose = () => {
    setShowTosModal(false);
    setHasReadTos(true);
  };

  const handlePrivacyOpen = () => {
    setShowPrivacyModal(true);
  };

  const handlePrivacyClose = () => {
    setShowPrivacyModal(false);
    setHasReadPrivacy(true);
  };

  const handleCheckboxChange = (e) => {
    if (hasReadTos && hasReadPrivacy) {
      setAgreedToTerms(e.target.checked);
      if (e.target.checked) {
        setErrors({ ...errors, terms: undefined });
      }
    }
  };

  const handleGoToSignIn = () => {
    dispatch(clearSignupSuccess());
    navigate('/sign-in');
  };

  // Calculate password strength
  const getPasswordStrength = (password) => {
    if (!password) return null;

    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
    if (!/\s/.test(password)) score++; // No spaces

    const percentage = (score / 6) * 100;

    if (score <= 2) return {
      level: 'Weak',
      color: '#ff4757',
      percent: percentage,
      status: 'exception'
    };
    if (score <= 4) return {
      level: 'Medium',
      color: '#ffa502',
      percent: percentage,
      status: 'normal'
    };
    return {
      level: 'Strong',
      color: '#23c552',
      percent: percentage,
      status: 'success'
    };
  };

  const passwordStrength = getPasswordStrength(signUpData.password);

  return (
    <div className={styles.signUpForm}>
      <div className={styles.signUpChild}>
        <div className={styles.aceYourTestContainer}>
          <p className={styles.aceYourTest}>Ace your test</p>
          <p className={styles.aceYourTest}>with Prepal</p>
        </div>
        <img
          className={styles.maskGroupIcon}
          alt=""
          src={require("../../publicAsset/media/book 1.svg").default}
        />
      </div>
      <div className={styles.prepalLogo3Parent}>
        <img
          className={styles.prepalLogo3}
          alt=""
          src={require("../../publicAsset/media/Prepal Logo 3.svg").default}
        />
        <div className={styles.frameParent}>
          <div className={styles.signUpWrapper}>
            <div className={styles.signUp}>{isSignupSuccess ? 'Account Created!' : 'Sign up'}</div>
          </div>
          {!isSignupSuccess ? (
            <form className={styles.frameGroup} onSubmit={handleSubmit}>
              <div className={styles.frameWrapper}>
                <div className={styles.inputParent}>
                  {userError && (
                    <div style={{ color: 'red', fontSize: '0.875rem', marginBottom: '1rem' }}>
                      {userError}
                    </div>
                  )}
                  {errors.general && (
                    <div style={{ color: 'red', fontSize: '0.875rem', marginBottom: '1rem' }}>
                      {errors.general}
                    </div>
                  )}
                  <div className={styles.input}>
                    <input
                      className={styles.yourEmailAndPassword}
                      name="email"
                      type="email"
                      placeholder="Your Email"
                      required
                      onChange={(e) => setSignUpData({ ...signUpData, email: e.target.value })}
                      style={{ borderColor: errors.email ? 'red' : '' }}
                    />
                  </div>
                  {errors.email && (
                    <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '-0.5rem' }}>{errors.email}</div>
                  )}
                  <div className={styles.input}>
                    <input
                      className={styles.yourEmailAndPassword}
                      name="password"
                      type="password"
                      placeholder="Your Password"
                      required
                      onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })}
                      style={{ borderColor: errors.password ? 'red' : '' }}
                    />
                  </div>
                  {/* Password Strength Indicator */}
                  {signUpData.password && passwordStrength && (
                    <div className={styles.passwordStrengthContainer}>
                      <Progress
                        percent={passwordStrength.percent}
                        strokeColor={passwordStrength.color}
                        showInfo={false}
                        size="small"
                        style={{ flex: 1 }}
                      />
                      <div
                        className={styles.strengthLabel}
                        style={{ color: passwordStrength.color }}
                      >
                        {passwordStrength.level}
                      </div>
                    </div>
                  )}
                  {errors.password && (
                    <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '-0.5rem' }}>{errors.password}</div>
                  )}
                  {signUpData.password && validateStrongPassword(signUpData.password) && (
                    <div className={styles.passwordHint}>
                      <div style={{ color: '#23c552', fontSize: '0.75rem', marginTop: '-0.5rem' }}>
                        ✓ Password meets all requirements
                      </div>
                    </div>
                  )}
                  {signUpData.password && !validateStrongPassword(signUpData.password) && (
                    <div className={styles.passwordRequirements}>
                      <div style={{ fontSize: '0.75rem', color: '#777e90', marginTop: '-0.5rem' }}>
                        Password must include:
                        <ul style={{ margin: '0.25rem 0 0 1.25rem', padding: 0 }}>
                          <li style={{ color: /[A-Z]/.test(signUpData.password) ? '#23c552' : '#777e90' }}>
                            {/[A-Z]/.test(signUpData.password) ? '✓' : '○'} At least one uppercase letter
                          </li>
                          <li style={{ color: /[a-z]/.test(signUpData.password) ? '#23c552' : '#777e90' }}>
                            {/[a-z]/.test(signUpData.password) ? '✓' : '○'} At least one lowercase letter
                          </li>
                          <li style={{ color: /\d/.test(signUpData.password) ? '#23c552' : '#777e90' }}>
                            {/\d/.test(signUpData.password) ? '✓' : '○'} At least one number
                          </li>
                          <li style={{ color: /[!@#$%^&*(),.?":{}|<>]/.test(signUpData.password) ? '#23c552' : '#777e90' }}>
                            {/[!@#$%^&*(),.?":{}|<>]/.test(signUpData.password) ? '✓' : '○'} At least one special character
                          </li>
                          <li style={{ color: signUpData.password.length >= 8 ? '#23c552' : '#777e90' }}>
                            {signUpData.password.length >= 8 ? '✓' : '○'} Minimum 8 characters
                          </li>
                          <li style={{ color: !/\s/.test(signUpData.password) ? '#23c552' : '#ff4757' }}>
                            {!/\s/.test(signUpData.password) ? '✓' : '✗'} No spaces allowed
                          </li>
                        </ul>
                      </div>
                    </div>
                  )}
                  <div className={styles.input}>
                    <input
                      className={styles.yourEmailAndPassword}
                      name="confirmPassword"
                      type="password"
                      placeholder="Confirm Password"
                      required
                      onChange={(e) => setSignUpData({ ...signUpData, confirmPassword: e.target.value })}
                      style={{ borderColor: errors.confirmPassword ? 'red' : '' }}
                    />
                  </div>
                  {errors.confirmPassword && (
                    <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '-0.5rem' }}>{errors.confirmPassword}</div>
                  )}
                </div>
              </div>
              <div className={styles.buttonsParent}>
                {/* Terms Agreement Checkbox */}
                <div className={styles.termsCheckboxContainer}>
                  <div className={styles.checkBoxWrapper}>
                    <input
                      type="checkbox"
                      id="agreeToTerms"
                      checked={agreedToTerms}
                      onChange={handleCheckboxChange}
                      disabled={!hasReadTos || !hasReadPrivacy}
                      className={styles.termsCheckbox}
                    />
                    <label htmlFor="agreeToTerms" className={styles.checkboxLabel}>
                      I have read and agree to the{' '}
                      <span className={styles.termsLink} onClick={handleTosOpen}>
                        Terms of Service
                      </span>
                      {' '}and{' '}
                      <span className={styles.termsLink} onClick={handlePrivacyOpen}>
                        Privacy Policy
                      </span>
                    </label>
                  </div>
                  {errors.terms && (
                    <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                      {errors.terms}
                    </div>
                  )}
                  {!hasReadTos || !hasReadPrivacy ? (
                    <div style={{ color: '#777e90', fontSize: '0.75rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                      Please click and read both documents to enable the checkbox
                    </div>
                  ) : null}
                </div>

                <div className={styles.byContinuingYouContainer}>
                  <p className={styles.aceYourTest}>
                    {`By continuing, you agree to our `}
                    <span className={styles.termsOfUse} onClick={handleTosOpen}>Terms of Use</span>
                  </p>
                  <p className={styles.aceYourTest}>
                    {`and `}
                    <span className={styles.termsOfUse} onClick={handlePrivacyOpen}>Privacy Policy</span>
                  </p>
                </div>

                <button type="submit" className={styles.buttons} disabled={isLoading || !agreedToTerms} style={{ opacity: (isLoading || !agreedToTerms) ? 0.7 : 1, cursor: (isLoading || !agreedToTerms) ? 'not-allowed' : 'pointer' }}>
                  <div className={styles.signUpBtn}>{isLoading ? 'Signing Up...' : 'Sign Up'}</div>
                </button>
                {isFeatureEnabled(FEATURES.SOCIAL_SIGNUP) && (
                  <>
                    <div className={styles.lineParent}>
                      <img
                        className={styles.groupChild}
                        alt=""
                        src={require("../../publicAsset/media/book 1.svg").default}
                      />
                      <div className={styles.orWrapper}>
                        <div className={styles.or}>Or</div>
                      </div>
                    </div>
                    <div className={styles.socialSignInParent}>
                      <div className={styles.socialSignIn}>
                        <img
                          className={styles.socialIcons}
                          alt="Google Icons.svg"
                          src={require("../../publicAsset/media/Platform=Google, Color=Original.svg").default}
                        />
                        <div className={styles.yourEmail}>With Google</div>
                      </div>
                      <div className={styles.socialSignIn}>
                        <img
                          className={styles.socialIcons}
                          alt="Facebook Icons.svg"
                          src={require("../../publicAsset/media/Platform=Facebook, Color=Original.svg").default}
                        />
                        <div className={styles.yourEmail}>With Facebook</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </form>
          ) : (
            <div className={styles.successContainer}>
              <div className={styles.successTitle}>🎉 Registration Successful!</div>
              <div className={styles.successMessage}>
                Your account has been created successfully!
                <br /><br />
                <span className={styles.successEmailText}>
                  Please check your email to confirm your account creation.
                </span>
                <br /><br />
                If you don't receive the confirmation email, your registration may be incomplete.
              </div>
              <button
                className={styles.goToSignInBtn}
                onClick={handleGoToSignIn}
              >
                <Link to="/sign-in" className={styles.goToSignInBtnText} style={{ textDecoration: 'none', color: 'inherit' }}>
                  Go to Sign In
                </Link>
              </button>
            </div>
          )}
        </div>
        {!isSignupSuccess && (
          <div className={styles.youHaveAnAccountParent}>
            <div className={styles.youHaveAn}>{`You have an account? `}</div>
            <div className={styles.signInWrapper}>
              <Link to="/sign-in" className={styles.didYouForget}>
                Sign In
              </Link>
            </div>
          </div>
        )}

        {/* Modals */}
        <TermsOfServiceModal isOpen={showTosModal} onClose={handleTosClose} />
        <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={handlePrivacyClose} />
      </div>
    </div>
  );
};

export default SignUpFormComponent;
